import React, { useMemo, useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import CountryCodeDropdown from './CountryCodeDropdown';

const UserAccountsFilter = ({ initialFilters = { activeOnly: null, country: '' }, onApplyFilters, onResetFilters, initiallyOpen = false, className = '' }) => {
    const { th } = useLanguage();
    const [filterOpen, setFilterOpen] = useState(initiallyOpen);

    const defaults = useMemo(
        () => ({
            firstName: '',
            lastName: '',
            email: '',
            dealerName: '',
            activeOnly: null,
            country: ''
        }),
        []
    );

    const [filters, setFilters] = useState({ ...defaults, ...initialFilters });

    useEffect(() => {
        setFilters((prev) => ({ ...prev, ...initialFilters }));
    }, [JSON.stringify(initialFilters)]);

    const toggle = () => setFilterOpen((p) => !p);
    const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    const handleReset = () => {
        const reset = { ...defaults, ...initialFilters };
        setFilters(reset);
        onResetFilters && onResetFilters();
    };

    const handleApply = () => {
        const payload = {
            firstName: (filters.firstName || '').trim(),
            lastName: (filters.lastName || '').trim(),
            email: (filters.email || '').trim(),
            dealerName: (filters.dealerName || '').trim(),
            activeOnly: filters.activeOnly == null ? null : !!filters.activeOnly,

            country: (filters.country || '').trim()
        };
        onApplyFilters && onApplyFilters(payload);
    };

    const onEnterApply = (e) => e.key === 'Enter' && handleApply();

    return (
        <div className={`generic-table-filter ${className}`}>
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button icon="pi pi-filter" label={th('filter.advancedFilter')} onClick={toggle} outlined={!filterOpen} />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('filter.advancedFilters')}</div>

                    <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('userAccounts.firstName')}</label>
                                <InputText value={filters.firstName || ''} onChange={(e) => update('firstName', e.target.value)} placeholder={th('userAccounts.enterFirstName')} className="w-full" onKeyDown={onEnterApply} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('userAccounts.lastName')}</label>
                                <InputText value={filters.lastName || ''} onChange={(e) => update('lastName', e.target.value)} placeholder={th('userAccounts.enterLastName')} className="w-full" onKeyDown={onEnterApply} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('userAccounts.email')}</label>
                                <InputText value={filters.email || ''} onChange={(e) => update('email', e.target.value)} placeholder={th('userAccounts.enterEmail')} className="w-full" onKeyDown={onEnterApply} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('userAccounts.dealerName')}</label>
                                <InputText value={filters.dealerName || ''} onChange={(e) => update('dealerName', e.target.value)} placeholder={th('userAccounts.enterDealerName')} className="w-full" onKeyDown={onEnterApply} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('userAccounts.country')}</label>
                                <CountryCodeDropdown value={filters.country} onChange={(code) => update('country', code || '')} placeholder={th('common.countrySelectPlaceholder')} className="w-full" />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <div className="flex align-items-center mt-1">
                                    <Checkbox inputId="activeOnly" checked={filters.activeOnly === true} onChange={(e) => update('activeOnly', e.checked)} />
                                    <label htmlFor="activeOnly" className="ml-2 text-sm">
                                        {th('userAccounts.activeOnly')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3">
                        <Button label={th('filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={handleReset} type="button" />
                        <Button label={th('filter.apply')} icon="pi pi-check" onClick={handleApply} type="button" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAccountsFilter;
